<template>
  <v-dialog v-model="localShow" max-width="700px">
    <v-card class="dialog-card">
      <!-- Fixed Header -->
      <v-card-title class="d-flex justify-space-between align-center bg-primary text-white sticky-header">
        <span>
          <v-icon class="mr-2">mdi-information</v-icon>
          Subscriber Status - MVNO
        </span>
        <v-btn color="error" variant="flat" size="small" @click="close">
          Close
        </v-btn>
      </v-card-title>

      <!-- Scrollable Content -->
      <v-card-text class="pa-4 scrollable-content">
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <template v-if="data && !loading">
          <v-alert v-if="data.error" type="error" class="mb-4">
            {{ data.message }}
          </v-alert>

          <template v-else>
            <!-- Expiration Date highlight -->
            <v-alert
              v-if="data.data && data.data.expiration_date"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-calendar-clock</v-icon>
                <span>
                  <strong>Subscriber Expiration Date:</strong>
                  {{ data.data.expiration_date }}
                </span>
              </div>
            </v-alert>

            <!-- MVNO Subscriber Data -->
            <h4 class="mb-2">Date MVNO</h4>
            <v-table density="compact" class="mb-4">
              <tbody>
                <tr v-for="(value, key) in data.data" :key="key">
                  <td class="font-weight-medium" width="40%">{{ formatKey(key) }}</td>
                  <td>
                    <template v-if="value === null || value === undefined">
                      <span class="text-grey">-</span>
                    </template>
                    <template v-else-if="typeof value === 'boolean'">
                      <v-chip :color="value ? 'success' : 'error'" size="x-small">
                        {{ value ? 'Da' : 'Nu' }}
                      </v-chip>
                    </template>
                    <template v-else-if="key.includes('balance') || key.includes('credit')">
                      <span class="font-weight-bold text-success">{{ Array.isArray(value) ? `${value[0]} ${value[2] || ''}`.trim() : value }}</span>
                    </template>
                    <template v-else-if="key.includes('date') || key.includes('expir')">
                      <span class="text-info">{{ value }}</span>
                    </template>
                    <template v-else-if="typeof value === 'object'">
                      <pre class="text-caption bg-grey-lighten-4 pa-2 rounded">{{ JSON.stringify(value, null, 2) }}</pre>
                    </template>
                    <template v-else>
                      {{ value }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- Updates Applied -->
            <template v-if="data.updates_applied && Object.keys(data.updates_applied).length > 0">
              <h4 class="mb-2">Actualizari aplicate local</h4>
              <v-table density="compact" class="mb-4">
                <tbody>
                  <tr v-for="(value, key) in data.updates_applied" :key="key">
                    <td class="font-weight-medium" width="40%">{{ formatKey(key) }}</td>
                    <td class="text-success">{{ value }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <!-- Other top-level data (excluding known keys) -->
            <template v-if="otherData && Object.keys(otherData).length > 0">
              <h4 class="mb-2">Alte informatii</h4>
              <v-table density="compact" class="mb-4">
                <tbody>
                  <tr v-for="(value, key) in otherData" :key="key">
                    <td class="font-weight-medium" width="40%">{{ formatKey(key) }}</td>
                    <td>
                      <template v-if="value === null || value === undefined">
                        <span class="text-grey">-</span>
                      </template>
                      <template v-else-if="typeof value === 'boolean'">
                        <v-chip :color="value ? 'success' : 'error'" size="x-small">
                          {{ value ? 'Da' : 'Nu' }}
                        </v-chip>
                      </template>
                      <template v-else-if="typeof value === 'object'">
                        <pre class="text-caption bg-grey-lighten-4 pa-2 rounded">{{ JSON.stringify(value, null, 2) }}</pre>
                      </template>
                      <template v-else>
                        {{ value }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <v-alert v-if="data.sim_updated" type="success" density="compact" class="mt-2">
              SIM-ul local a fost actualizat cu datele din MVNO
            </v-alert>

            <v-alert v-if="data.message && !data.error" type="info" density="compact" class="mt-2">
              {{ data.message }}
            </v-alert>
          </template>
        </template>

        <template v-if="!data && !loading">
          <v-alert type="info" density="compact">
            Nu exista date disponibile
          </v-alert>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SubscriberStatusDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'update:show'],
  computed: {
    localShow: {
      get() {
        return this.show
      },
      set(val) {
        if (!val) {
          this.$emit('close')
          this.$emit('update:show', false)
        }
      }
    },
    // Extract other data that is not in known keys
    otherData() {
      if (!this.data) return null
      const knownKeys = ['error', 'message', 'data', 'updates_applied', 'sim_updated']
      const other = {}
      for (const key in this.data) {
        if (!knownKeys.includes(key) && this.data[key] !== null && this.data[key] !== undefined) {
          other[key] = this.data[key]
        }
      }
      return Object.keys(other).length > 0 ? other : null
    }
  },
  methods: {
    close() {
      this.$emit('close')
      this.$emit('update:show', false)
    },
    formatKey(key) {
      if (!key) return ''
      // Convert snake_case or camelCase to readable format
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
    }
  }
}
</script>

<style scoped>
.dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.scrollable-content {
  overflow-y: auto;
  flex-grow: 1;
}

pre {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}
</style>
